import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import Summarization from 'models/Summarization';
import { deleteSummarization } from 'services/api/summarizations';
import { useToastActions } from 'stores/toastStore';
import { BodySmall, BodySmallBold } from 'styles/typography';

export type DeleteSummaryModalProps = {
  summarization: Summarization;
  onCancel: () => void;
};

export default function DeleteSummaryModal({ summarization, onCancel }: DeleteSummaryModalProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate: deleteSummary, isPending } = useMutateWithToast({
    mutationFn: deleteSummarization,
  });

  const confirmDeleteSummary = async () => {
    deleteSummary(
      { id: summarization.id },
      {
        onSuccess: () => {
          addToast({ text: 'Summary has been successfully deleted!' });

          onCancel();

          if (pathname.endsWith('/summarizations')) {
            invalidateQueries({ summarizations: true });
          } else {
            navigate('/summarizations');
          }
        },
      }
    );
  };

  return (
    <StandardModal colorTheme={ColorThemeName.Danger} onCancel={onCancel} title='Delete Summary?'>
      <SummaryTitle>Summary: {summarization.title}</SummaryTitle>

      <Content>
        <Paragraph>
          <b>Are you sure you want to delete this summary?</b> Once deleted, summaries can no longer be accessed in
          Olio.
        </Paragraph>
      </Content>
      <StyledButtonGroup>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='danger' onClick={confirmDeleteSummary} loading={isPending}>
          Delete Summary
        </Button>
      </StyledButtonGroup>
    </StandardModal>
  );
}

const StyledButtonGroup = styled(ButtonGroup)`
  justify-content: flex-end;
`;

const Content = styled.div`
  margin: 26px 0 50px;
`;

const Paragraph = styled(BodySmall)`
  display: block;
`;

const SummaryTitle = styled(BodySmallBold)`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;
